import React, { useState, useEffect, useMemo } from 'react';

import styles from './User.module.scss';

import { userService } from 'src/app/services';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { userActions } from 'src/app/redux/actions';
import { RegisterDataType, UsersResultType } from 'src/types/services/user-service.type';
import UserForm from 'src/app/components/Forms/UserForm/userForm';

import { deleteConfirmTitles, getRolesList, Item, UserPropsType } from './user.config';
import { displaySuccessStatusMessage } from 'src/app/helpers/status.helper';

import { checkPermissionList } from 'src/app/helpers/permissions.helper';
import { PermissionEnum } from 'src/enums/permission.enum';
import { UserRoleType } from 'src/types/redux/userrole-permission.redux.type';

import { v4 } from 'uuid';
import useSearch from 'src/app/hooks/useSearch';
import CustomTable from 'src/app/components/CustomTable/CustomTable';
import { useTranslation } from 'react-i18next';
import { isUserOperationsDisabledForCurrentUser } from 'src/app/helpers/isUserOperationsDisabled.helper';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Popover from 'antd/es/popover';
import Typography from 'antd/es/typography';
import Popconfirm from 'antd/es/popconfirm';
import Button from 'antd/es/button/button';

const { Search } = Input;

const UserTab: React.FC<UserPropsType> = (props: UserPropsType) => {
  const { t } = useTranslation();

  const defaultUserData = {
    id: '',
    userName: '',
    name: undefined,
    surname: undefined,
    email: undefined,
    telephone: undefined,
    organization: undefined,
    events: [],
    password: '',
    passwordConfirmation: '',
    sessionTime: undefined,
    isLocalUser: true,
  };
  const dispatch = useDispatch();

  const [addUserModal, setAddUserModal] = useState<boolean>(false);
  const [modalInEditMode, setModalInEditMode] = useState<boolean>(false);

  const [form] = Form.useForm();
  const [dataForUser, setDataForUser] = useState<Item[]>([]);
  const [userListAllData, setUserListAllData] = useState<UsersResultType[]>([]);
  const [userListFilteredData, setUserListFilteredData] = useState<Item[] | null>(null);
  const [userDataInForm, setUserDataInForm] = useState<RegisterDataType>(defaultUserData);
  const { searchValue, onSearch } = useSearch();
  const [currentFilters, setCurrentFilters] = useState({});

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const { eventList } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const { ontologies } = useSelector((state: reduxRootTypes.RootStateType) => state.ontologyState);
  const { usersList } = useSelector((state: reduxRootTypes.RootStateType) => state.userListState);
  const [isLoading, setIsLoading] = useState(false);

  const onClickAddPlayerType = () => {
    if (modalInEditMode) {
      setUserDataInForm(defaultUserData);
    }
    setAddUserModal(true);
    setModalInEditMode(false);
  };

  const lastVisitedKey = userDataInForm.id || '';

  const currentUserAdminEvents = useMemo(() => {
    return userState.events.filter((e) => e.roles.some((r) => r.abbreviation === 'Admin'));
  }, [userState, eventList]);

  useEffect(() => {
    if (usersList.length) return;

    setIsLoading(true);
    const getUserListData = async () => {
      try {
        const userListData = await userService.lookupUsersTable({ blockSpinner: true });
        dispatch(userActions.setListInformation(userListData.data.rows));
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    };

    getUserListData();
  }, []);

  useEffect(() => {
    const clearData: Item[] = [];
    userListAllData.forEach((item) => {
      clearData.push({
        id: item.id,
        userName: item.userName,
        name: item.name,
        surname: item.surname,
        email: item.email,
        telephone: item.telephone,
        organization: item.organization,
        userRoles: item.userRoles,
        events: item.events,
      });
    });

    setDataForUser(clearData);
  }, [userListAllData]);

  const getUserData = async () => {
    try {
      // const userData = await userService.lookupUsersTable({
      //   limit: paginationConfig.maxDatabasePageSize.toString(),
      //   offset: ((currentPage - 1) * paginationConfig.maxDatabasePageSize).toString(),
      //   value: searchValue.length > 0 ? searchValue : undefined,
      //   blockSpinner: true,
      // });

      const userDataList: any[] = [];

      if (searchValue.length > 0) {
        const searchUserList = usersList?.filter((obj) => {
          const userName = obj.userName?.toLowerCase() || '';
          const name = obj.name?.toLowerCase() || '';
          const surname = obj.surname?.toLowerCase() || '';

          return (
            userName.includes(searchValue.toLowerCase()) ||
            name.includes(searchValue.toLowerCase()) ||
            surname.includes(searchValue.toLowerCase())
          );
        });

        searchUserList?.forEach((item) => {
          const userRolesData: UserRoleType[] = [];
          item.events.forEach((event) => {
            event.roles.forEach((role) => {
              if (!userRolesData.find((x) => x.id === role.id)) {
                userRolesData.push(role);
              }
            });
          });

          userDataList.push({
            ...item,
            userRoles: userRolesData,
          });
        });
      } else {
        usersList?.forEach((item) => {
          const userRolesData: UserRoleType[] = [];
          item.events.forEach((event) => {
            event.roles.forEach((role) => {
              if (!userRolesData.find((x) => x.id === role.id)) {
                userRolesData.push(role);
              }
            });
          });
          userDataList.push({
            ...item,
            userRoles: userRolesData,
          });
        });
      }
      setUserListAllData(userDataList);
      // setDataCount(userData.data.count);
      // dispatch(userActions.setListInformation(userListData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (usersList) {
      getUserData();
    }
  }, [ontologies, eventList, searchValue, usersList]);

  const edit = (record: Partial<Item> & { id: React.Key }) => {
    const item = userListAllData.find((o) => o.id === record.id);
    if (item) {
      setUserDataInForm({
        ...item,
        events: [
          ...item.events.map((e) => ({
            ...e,
            formId: v4(),
            roles: [...e.roles.map((r) => r.id)],
          })),
        ],
      });

      setModalInEditMode(true);
      setAddUserModal(true);
    }
  };

  const deleteUser = async (deletedItem: Item) => {
    try {
      if (deletedItem.id === userDataInForm.id) {
        setUserDataInForm(defaultUserData);
        // TODO: açık olan popup sayfasının kapanması için değişkeni ekle.
      }

      const updatedUserList = userListAllData.filter((item) => item.id !== deletedItem.id);

      setUserListAllData(updatedUserList);

      dispatch(userActions.userDeleted(deletedItem.id));

      await userService.deleteUsers({ id: deletedItem.id });

      displaySuccessStatusMessage(t('success.deleted'));
    } catch (error) {
      console.log(error);
    }
  };

  const columnsWithOperations = [
    {
      title: t('administration.users.username'),
      dataIndex: 'userName',
      width: '8%',
      sorter: (a: any, b: any) => a.userName?.localeCompare(b.userName),
    },
    {
      title: t('administration.users.name'),
      dataIndex: 'name',
      width: '10%',
      sorter: (a: any, b: any) => a.name?.localeCompare(b.name),
    },
    {
      title: t('administration.users.surname'),
      dataIndex: 'surname',
      width: '10%',
      sorter: (a: any, b: any) => a.surname?.localeCompare(b.surname),
    },
    {
      title: t('administration.ontologies.email'),
      dataIndex: 'email',
      width: '12%',
    },
    {
      title: t('administration.userRights.userRoleName'),
      dataIndex: 'userRoles',
      width: '10%',
      onFilter: (value: any, record: any) => record.userRoles.map((e: any) => e.abbreviation === value).includes(true),
      filters: getRolesList(dataForUser, ontologies),
      render: (value: any, record: any) => (
        <Popover content={record.userRoles.map((e: any) => e.abbreviation).join(', ')} trigger="hover">
          <div className={styles.tableTitleStyle}>{record.userRoles.map((e: any) => e.abbreviation).join(', ')}</div>
        </Popover>
      ),
    },
    {
      title: t('general.eventId'),
      dataIndex: 'event',
      filters: eventList.map((e) => ({
        text: e.eventId,
        value: e.eventId,
      })),
      onFilter: (value: any, record: any) => record.events.some((e: any) => e.eventId.indexOf(value) === 0),
      width: '20%',
      render: (value: any, record: any) => {
        return record.events.map((e: any) => e.eventId).join();
      },
    },
    {
      title: t('general.operation.operation'),
      dataIndex: 'operation',
      width: '6%',
      fixed: 'right' as const,
      render: (_: any, record: Item) => {
        return (
          <div>
            <Popover content={<div>{t('general.operation.edit')}</div>} trigger="hover">
              <Typography.Link
                onClick={() => edit(record)}
                disabled={isUserOperationsDisabledForCurrentUser(
                  usersList,
                  currentUserAdminEvents,
                  userState,
                  record.userName,
                  checkPermissionList(PermissionEnum.UserUpdate),
                )}
              >
                <img className="edit-icon" src="/assets/editIcon.png" alt="edit" />
              </Typography.Link>
            </Popover>
            <Popover content={<div>{t('general.operation.delete')}</div>} trigger="hover">
              <Popconfirm
                placement="bottom"
                title={deleteConfirmTitles().title}
                okText={deleteConfirmTitles().okText}
                cancelText={deleteConfirmTitles().cancelText}
                icon=""
                onCancel={() => deleteUser(record)}
                disabled={isUserOperationsDisabledForCurrentUser(
                  usersList,
                  currentUserAdminEvents,
                  userState,
                  record.userName,
                  checkPermissionList(PermissionEnum.UserDelete),
                )}
              >
                <img
                  className="delete-icon"
                  src="/assets/deleteIcon.png"
                  alt="delete"
                  aria-disabled={isUserOperationsDisabledForCurrentUser(
                    usersList,
                    currentUserAdminEvents,
                    userState,
                    record.userName,
                    checkPermissionList(PermissionEnum.UserDelete),
                  )}
                />
              </Popconfirm>
            </Popover>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columnsWithOperations.map((col) => {
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === 'type' ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    };
  });

  useEffect(() => {
    if (dataForUser) {
      if (currentFilters && Object.keys(currentFilters).length > 0) {
        handleTableChange(currentFilters);
      } else {
        setUserListFilteredData(null);
      }
    }
  }, [dataForUser]);

  const handleTableChange = (filters: any) => {
    const { userRoles, event } = filters;

    const allFiltersNull =
      ((!filters.userRoles || filters.userRoles.length === 0) && !filters.event) || filters.event.length === 0;

    if (allFiltersNull) {
      setUserListFilteredData(null);
      setCurrentFilters({});
    } else {
      let filteredData = dataForUser;

      if (userRoles && userRoles.length > 0) {
        filteredData = filteredData.filter((f) => f.userRoles.map((e) => e.abbreviation).includes(userRoles[0]));
      }

      if (event && event.length > 0) {
        filteredData = filteredData.filter((f) => f.events.map((e) => e.eventId).includes(event[0]));
      }

      setUserListFilteredData(filteredData);
      setCurrentFilters(filters);
    }
  };

  return (
    <>
      <div className="database-main-container">
        <div className="database-container">
          <div className="database-add-button-container">
            <Button
              className="database-add-button"
              onClick={onClickAddPlayerType}
              disabled={checkPermissionList(PermissionEnum.UserCreate)}
            >
              <div className="database-add-button-inner-container">
                <p className="database-add-button-text">{t('administration.users.addUser')}</p>
              </div>
            </Button>
            <Search
              placeholder={t('general.search') || ''}
              allowClear
              onSearch={onSearch}
              onChange={onSearch}
              className="database-search-box"
            />
          </div>
          <Form id="formId" form={form} component={false}>
            {/* <Table
              bordered
              dataSource={dataForUser.filter((obj) => obj.userName !== 'root')}
              columns={mergedColumns}
              rowClassName={(record) => (record.id === lastVisitedKey.toString() ? 'last-visited-row' : '')}
              pagination={{
                pageSize: 10,
                pageSizeOptions: [10],
                showSizeChanger: true,
                showTotal: () => `Total ${dataForUser.length} items`,
                onChange: cancel,
              }}
            /> */}
            <CustomTable
              dataSource={
                userListFilteredData?.filter((obj) => obj.userName.toLocaleLowerCase() !== 'root') ||
                dataForUser.filter((obj) => obj.userName.toLocaleLowerCase() !== 'root')
              }
              columns={mergedColumns}
              onChange={handleTableChange}
              rowClassName={(record) => (record.id === lastVisitedKey.toString() ? 'last-visited-row' : '')}
              loading={isLoading}
            />
          </Form>
        </div>
      </div>

      {addUserModal && (
        <UserForm
          onClickOpen
          userForm={userDataInForm}
          setOnClickOpen={setAddUserModal}
          setEditModal={modalInEditMode}
        />
      )}
    </>
  );
};

export default UserTab;
